/**
 * Restaurant Data Hook
 *
 * Fetches and manages restaurant data including logo, brand colors, and name
 * Follows architecture: Hook → Service → Flask Backend → Supabase
 */

import { useEffect, useState } from 'react'
import { useRestaurantStore } from '@restaurant/core'
import { RestaurantService } from '@restaurant/core'
import { getRestaurantId } from '@restaurant/core'
import { ApiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'
import { getAppDebugMode } from '../config/debugMode'
import { isShowcase } from '../showcase/showcase'


const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_FLASK_BASE_URL || 'https://api.thalione.tech',
})

const restaurantService = new RestaurantService({ apiClient })

export interface UseRestaurantDataReturn {
  isLoading: boolean
  error: string | null
  restaurantId: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch and manage restaurant data
 * Loads restaurant information from Flask backend and stores in restaurant store
 */
export function useRestaurantData(restaurantId?: string): UseRestaurantDataReturn {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentRestaurantId, setCurrentRestaurantId] = useState<string | null>(null)

  const { setRestaurant, setLocation, setLocations, setError: setStoreError } = useRestaurantStore()

  const fetchRestaurantData = async (id: string) => {
    setIsLoading(true)
    setError(null)

    safeGetLogger()?.debug('Fetching restaurant data', { domain: 'config', id })

    try {
      const restaurant = await restaurantService.getRestaurant(id)

      safeGetLogger()?.config('brand_loaded', { name: restaurant.name, source: 'api' })

      // Store in restaurant store (merge debug flag immutably)
      setRestaurant(getAppDebugMode() ? { ...restaurant, isDebugMode: true } : restaurant)
      setCurrentRestaurantId(id)

      // Store all locations in the store
      const firstLocation = restaurant.locations?.[0]
      if (restaurant.locations && firstLocation) {
        setLocations(restaurant.locations)
        // Only set location if none was already selected (e.g. by main.tsx init)
        const { currentLocation } = useRestaurantStore.getState()
        if (!currentLocation) {
          setLocation(firstLocation)
        }
        safeGetLogger()?.debug('Locations stored', { domain: 'config', count: restaurant.locations.length })
      }
    } catch (err) {
      safeGetLogger()?.error('Failed to fetch restaurant data', { domain: 'config', error: err instanceof Error ? err.message : String(err) })

      const errorMessage = 'Flask API not available - using fallback configuration'
      setError(errorMessage)
      setStoreError(errorMessage)

      safeGetLogger()?.config('brand_fallback', { reason: 'api_failed' })
    } finally {
      setIsLoading(false)
      safeGetLogger()?.debug('Restaurant data fetch complete', { domain: 'config' })
    }
  }

  const refetch = async () => {
    if (currentRestaurantId) {
      await fetchRestaurantData(currentRestaurantId)
    }
  }

  useEffect(() => {
    if (isShowcase()) return
    // Get restaurant ID from props or resolved tenant
    const id = restaurantId || getRestaurantId()

    fetchRestaurantData(id)
  }, [restaurantId])

  return {
    isLoading,
    error,
    restaurantId: currentRestaurantId,
    refetch,
  }
}
